import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  parseRequestsQuery,
  serializeRequestsQuery,
  type RequestsQueryState,
} from '../lib/adminRequests';
import { useSuperAdminStore } from '../store/useSuperAdminStore';

const DefaultLimit = 30;

const useRequestsQuery = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const query = useMemo(() => parseRequestsQuery(location.search), [location.search]);
  const [draftSearch, setDraftSearch] = useState(query.q);
  const updateQuery = useCallback((patch: Partial<RequestsQueryState>) => {
    const next = { ...query, ...patch };
    const search = serializeRequestsQuery(next);
    navigate({ pathname: '/super-admin/requests', search: search ? `?${search}` : '' });
  }, [navigate, query]);

  useEffect(() => setDraftSearch(query.q), [query.q]);
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      if (draftSearch !== query.q) {
        updateQuery({ q: draftSearch, ticket: query.ticket });
      }
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [draftSearch, query.q, query.ticket, updateQuery]);

  return { draftSearch, query, setDraftSearch, updateQuery };
};

const useRequestsData = (
  query: RequestsQueryState,
  updateQuery: (patch: Partial<RequestsQueryState>) => void
) => {
  const store = useSuperAdminStore();
  const {
    clearTicketDetail,
    fetchTickets,
    fetchUsers,
    loadTicketDetail,
    requestLoading,
    requestLoadingMore,
    requestNextCursor,
  } = store;
  const listRef = useRef<HTMLDivElement | null>(null);
  const pageSentinelRef = useRef<HTMLDivElement | null>(null);
  const assignees = useMemo(
    () => store.users.filter((user) => user.role === 'Super' && user.status === 'active'),
    [store.users]
  );

  useEffect(() => {
    void fetchTickets({
      category: query.category,
      limit: DefaultLimit,
      q: query.q,
      status: query.status,
    }).catch(() => undefined);
  }, [fetchTickets, query.category, query.q, query.status]);

  useEffect(() => {
    void fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (!query.ticket) {
      clearTicketDetail();
      return;
    }
    let active = true;
    void loadTicketDetail(query.ticket).catch(() => {
      if (active) {
        updateQuery({ ticket: null });
      }
    });
    return () => {
      active = false;
    };
  }, [clearTicketDetail, loadTicketDetail, query.ticket, updateQuery]);

  const loadMore = useCallback(() => {
    if (!requestNextCursor || requestLoading || requestLoadingMore) {
      return;
    }
    void fetchTickets({
      category: query.category,
      cursor: requestNextCursor,
      limit: DefaultLimit,
      q: query.q,
      status: query.status,
    }, 'append').catch(() => undefined);
  }, [
    fetchTickets,
    requestLoading,
    requestLoadingMore,
    requestNextCursor,
    query.category,
    query.q,
    query.status,
  ]);

  useEffect(() => {
    const sentinel = pageSentinelRef.current;
    if (!sentinel || !requestNextCursor || typeof IntersectionObserver === 'undefined') {
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        loadMore();
      }
    }, { root: listRef.current, rootMargin: '160px 0px' });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore, requestNextCursor]);

  return { ...store, assignees, listRef, loadMore, pageSentinelRef };
};

export const useRequestsRouteModel = () => {
  const queryModel = useRequestsQuery();
  const dataModel = useRequestsData(queryModel.query, queryModel.updateQuery);
  return { ...queryModel, ...dataModel };
};
